import { Component, forwardRef, Input, OnDestroy } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

const FLOK_NUMBER_INPUT_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FlokNumberInputComponent),
    multi: true
}

@Component({
    selector: 'app-flok-number-input',
    templateUrl: 'flok-number-input.html',
    styleUrls: ['flok-number-input.scss'],
    providers: [FLOK_NUMBER_INPUT_VALUE_ACCESSOR]
})
export class FlokNumberInputComponent implements ControlValueAccessor, OnDestroy {
    @Input() max?: number
    @Input() min?: number
    @Input() debounceMs = 300 // Debounce delay in milliseconds

    value = 0
    disabled = false

    private onChange: (value: number) => void = () => {}
    private onTouched: () => void = () => {}
    private debounceTimeout?: ReturnType<typeof setTimeout>

    ngOnDestroy(): void {
        this.clearDebounceTimeout()
    }

    // ControlValueAccessor implementation
    registerOnChange(onChange: (value: number) => void): void {
        this.onChange = onChange
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched
    }

    writeValue(value: number | null): void {
        if (this.disabled) return
        this.value = value ?? 0
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
    }

    // Public methods
    minus(): void {
        if (this.disabled) return
        if (this.min !== undefined && this.value <= this.min) return

        this.value--
        this.debouncedOnChange()
        this.onTouched()
    }

    plus(): void {
        if (this.disabled) return
        if (this.max !== undefined && this.value >= this.max) return

        this.value++
        this.debouncedOnChange()
        this.onTouched()
    }

    // Private methods
    private debouncedOnChange(): void {
        this.clearDebounceTimeout()

        this.debounceTimeout = setTimeout(() => {
            this.onChange(this.value)
        }, this.debounceMs)
    }

    private clearDebounceTimeout(): void {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout)
            this.debounceTimeout = undefined
        }
    }
}
